import React, { useCallback, useMemo, useState } from 'react';

import { useReactNativeProps, runningInRNWebView } from '@eika-infrastruktur/mobilbank-lib';
import { AttentionMessage } from '@eika/attention-message';
import { Button, LinkButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { Modal } from '@eika/modal';

import { Logger } from '@/domain/app';
import {
    AppleWalletService,
    PendingCardTokenForUI,
    PendingCardTokenItem,
    PendingCardTokensList,
} from '@/domain/digital-wallet';
import piwik from '@/piwik/piwik';
import PendingCardTokenList from '@/widgets/PendingCardTokens/PendingCardTokenList';
import PendingCardTokenModalWrapper from '@/widgets/PendingCardTokens/PendingCardTokenModalWrapper';
import usePendingCardTokenCookies from '@/widgets/PendingCardTokens/usePendingCardTokenCookies';

import applePay from '@domain/digital-wallet/components/apple-pay/ApplePay.svg';
import { useActivateDigitalWalletPendingCard } from '@domain/digital-wallet/hooks/use-digital-wallet-pending-cards';
import { DeviceType } from '@generated-types';
import useText from '@hooks/use-text';
import replaceCurlyWithJsx from '@utils/replaceCurlyWithJsx';

import './PendingCardTokens.scss';

type PendingCardTokensContentProps = {
    pendingCardTokens: PendingCardTokensList;
};

const toUICardToken = (item: PendingCardTokenItem): PendingCardTokenForUI => {
    if (item.source === 'te') {
        return {
            cardId: item.token.eikaCardId,
            imageUrl: item.card.imagePath,
            tokenId: item.token.eikaTokenId,
            cardName: item.card.product.name,
            cardNumber: item.card.number,
        };
    }

    return {
        cardId: item.card.id,
        imageUrl: item.card.imagePath,
        tokenId: item.token.id,
        cardName: item.card.product.name,
        cardNumber: item.card.cardNumber,
    };
};

export const PendingCardTokensContent = ({ pendingCardTokens }: PendingCardTokensContentProps) => {
    const text = useText();

    const nativeProps = useReactNativeProps();

    const enabled = useMemo(() => Boolean(nativeProps.supports?.applePay), [nativeProps]);
    const hasCards = pendingCardTokens.length > 0;

    const { activateCard, lastCardTriedAdded, statusArray, hasSuccess, hasLoading, invalidateAndClear } =
        useActivateDigitalWalletPendingCard(enabled, false);

    const statusModalState = useState(false);
    const [, setOpenStatusModal] = statusModalState;

    const [open, setOpen] = useState(true);
    const [showWarning, setShowWarning] = useState(false);

    const status = useMemo(() => {
        if (showWarning) return 'warning';
        if (!hasSuccess) return 'warning';
        return 'success';
    }, [showWarning, hasSuccess]);

    const { showModal, setCookies } = usePendingCardTokenCookies();

    const openAndShowWarning = useCallback(() => {
        setShowWarning(true);
        setOpenStatusModal(true);
    }, [setShowWarning, setOpenStatusModal]);

    const cleanup = useCallback(async () => {
        setCookies(status === 'success' || hasSuccess);
        setOpenStatusModal(false);
        await invalidateAndClear();
    }, [setOpenStatusModal, invalidateAndClear, setCookies, status, hasSuccess]);

    const canActivatePendingCard =
        showModal && runningInRNWebView && enabled && nativeProps.supports?.cookies === true && hasCards;

    const undo = () => {
        setOpenStatusModal(false);
        setShowWarning(false);
    };

    const activate = useCallback(
        (tokenId: string) => {
            const cardToken = pendingCardTokens.find((item) => {
                if (item.source === 'te') {
                    return item.token.eikaTokenId === tokenId;
                }
                return item.token.id === tokenId;
            });

            if (cardToken) {
                activateCard(cardToken);
            }
        },
        [activateCard, pendingCardTokens],
    );

    const groupedTokens: Record<DeviceType, PendingCardTokensList> = useMemo(() => {
        const tokens = AppleWalletService.groupPendingTokens(pendingCardTokens);
        Logger.info('wallet', 'Pending tokens info message', 'PendingCardTokensContent', {
            tokens,
        });
        return tokens;
    }, [pendingCardTokens]);

    if (canActivatePendingCard || hasSuccess || hasLoading) {
        return (
            <Modal
                appNamespace="kortbetjening-web"
                open={open}
                setOpen={setOpen}
                onClose={cleanup}
                data-testid="pending-card-modal"
            >
                <PendingCardTokenModalWrapper
                    status={status}
                    statusModalState={statusModalState}
                    done={() => setOpen(false)}
                    undo={undo}
                >
                    <section className="pending-card__modal-intro">
                        <img className="pending-card__modal-intro__applepay-image" src={applePay} alt={text.applePay} />
                        <h2 className="pending-card__modal-intro__title">{text.activateApplePayCard}</h2>
                        <span className="pending-card__modal-intro__description">{text.doYouWishToAddTheCard}</span>
                    </section>
                    {Object.keys(groupedTokens).map((key) => {
                        const deviceCards = groupedTokens[key].map(toUICardToken);

                        return (
                            <PendingCardTokenList
                                key={key}
                                /* TODO */
                                cardInfoLoading={false}
                                title={text[`applePayDevice:${key}`]}
                                pendingCards={deviceCards}
                                show={deviceCards.length > 0}
                                statusArray={statusArray}
                                activateCard={activate}
                            />
                        );
                    })}
                    <div className="pending-card__modal-submit-wrapper">
                        {lastCardTriedAdded && hasSuccess && (
                            <Button
                                onClick={() => setOpenStatusModal(true)}
                                data-testid="pending-cards-go-on-button"
                                data-track-id={piwik.applePay.applePayActivationModalGoOn.dataTrackId}
                                data-track-linkname={piwik.applePay.applePayActivationModalGoOn.dataTrackLinkname}
                            >
                                {text.goOn}
                            </Button>
                        )}

                        <LinkButton
                            disabled={hasLoading}
                            onClick={openAndShowWarning}
                            data-testid="pending-cards-cancel-button"
                            data-track-id={piwik.applePay.applePayActivationModalCancel.dataTrackId}
                            data-track-linkname={piwik.applePay.applePayActivationModalCancel.dataTrackLinkname}
                        >
                            {text.cancel}
                        </LinkButton>
                    </div>

                    {!hasSuccess && (
                        <AttentionMessage
                            className="pending-card__modal-attention-message"
                            icon={IconTypes.UTROPSTEGN_24}
                            type="default"
                            text={text.wasItYou}
                            clickableItem={
                                <span className="pending-card__modal-attention-message-extra-text">
                                    {replaceCurlyWithJsx(
                                        text.clickAndContactBank,
                                        <span
                                            className="pending-card__modal-attention-message-extra-text--italic"
                                            data-track-id={piwik.applePay.appleWalletWasItYou.dataTrackId}
                                            data-track-linkname={piwik.applePay.appleWalletWasItYou.dataTrackLinkname}
                                        >
                                            {text.cancel}
                                        </span>,
                                    )}
                                </span>
                            }
                        />
                    )}
                </PendingCardTokenModalWrapper>
            </Modal>
        );
    }

    return null;
};
